import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Html } from '@react-three/drei';
import * as THREE from 'three';

const CABLE_COLOR_HEX = {
  BLUE: '#3B82F6', BROWN: '#8B4513', 'GREEN-YELLOW': '#9ACD32', GREY: '#808080',
  BLACK: '#333333', WHITE: '#EEEEEE', RED: '#EF4444', YELLOW: '#F59E0B',
  GREEN: '#22C55E', ORANGE: '#F97316'
};
const insulationHex = c => CABLE_COLOR_HEX[String(c || '').toUpperCase()] || '#D9D2C5';

// One helical strand as a tube following a helix along the Y axis.
function Strand({ pitchRadius, coreRadius, phase, lay, turns, color }) {
  const geometry = useMemo(() => {
    const pts = [];
    const segs = Math.max(40, Math.round(turns * 24));
    const totalAngle = turns * Math.PI * 2;
    const height = turns * lay; // scene units along Y
    for (let i = 0; i <= segs; i++) {
      const t = i / segs;
      const a = phase + t * totalAngle;
      pts.push(new THREE.Vector3(Math.cos(a) * pitchRadius, t * height - height / 2, Math.sin(a) * pitchRadius));
    }
    const curve = new THREE.CatmullRomCurve3(pts);
    return new THREE.TubeGeometry(curve, segs, coreRadius, 12, false);
  }, [pitchRadius, coreRadius, phase, lay, turns]);

  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial color={color} roughness={0.6} metalness={0.1} />
    </mesh>
  );
}

const Rope3D = ({ model, height = 420 }) => {
  // physical core count = modeled cores (reduced-GY "3x35+16" has 4 cores, nc=3)
  const n = Math.max(model?.cores?.length || 0, model?.numberOfConductors || 0);
  const hasRope = model?.hasRope && n > 1;

  const scene = useMemo(() => {
    if (!hasRope) return null;
    // scene units: use mm directly, scaled so it reads well
    const scale = 1;
    const ropeR = (model.ropeDiameter / 2) * scale;
    const coreR = (model.coreOuterD / 2) * scale;
    // pitch: fit inside the rope but never so tight the cores overlap (4-in-3 rope)
    const pitchR = n <= 1 ? 0 : Math.max(ropeR - coreR, coreR / Math.sin(Math.PI / n));
    // lay length from the model (mm); clamp the DISPLAYED pas so big cables don't
    // stretch the helix into an unreadable near-vertical line.
    const layMm = model.layLength > 0 ? model.layLength : model.ropeDiameter * 12;
    const lay = Math.min(layMm, ropeR * 8) * scale;
    const turns = 3;
    return { ropeR, coreR, pitchR, lay, turns };
  }, [hasRope, model, n]);

  if (!hasRope || !scene) {
    return (
      <div style={{ height, background: '#0f1420', borderRadius: 6 }}
        className="d-flex align-items-center justify-content-center text-500 text-center px-3">
        {n <= 1 ? 'Single-core cable — no stranding' : 'Set a lay length (pas) to render the twisted rope'}
      </div>
    );
  }

  const { ropeR, coreR, pitchR, lay, turns } = scene;
  const camDist = Math.max(ropeR * 5, lay * turns * 0.7);

  return (
    <div style={{ height, background: '#0f1420', borderRadius: 6, overflow: 'hidden' }}>
      <Canvas camera={{ position: [camDist, camDist * 0.5, camDist], fov: 38 }} dpr={[1, 2]}>
        <color attach="background" args={['#0f1420']} />
        <ambientLight intensity={0.8} />
        <directionalLight position={[ropeR * 4, lay * 3, ropeR * 5]} intensity={1.1} />
        <directionalLight position={[-ropeR * 4, -lay, -ropeR * 3]} intensity={0.35} />
        <OrbitControls enablePan enableZoom target={[0, 0, 0]} />
        {Array.from({ length: n }, (_, i) => {
          const core = model.cores[Math.min(i, model.cores.length - 1)] || model.cores[0];
          return (
            <Strand
              key={i}
              pitchRadius={pitchR}
              coreRadius={(core?.outerD / 2) || coreR}
              phase={(i / n) * Math.PI * 2}
              lay={lay}
              turns={turns}
              color={insulationHex(core?.color)}
            />
          );
        })}
        <Html position={[0, -lay * turns * 0.6, 0]} center>
          <div style={{ color: '#9fb3c8', fontSize: 11, fontFamily: 'monospace', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
            {n} cores · rope Ø {model.ropeDiameter.toFixed(2)} mm · pas {model.layLength > 0 ? `${model.layLength} mm` : 'auto'} · take-up {model.takeUp.toFixed(3)}×
          </div>
        </Html>
      </Canvas>
    </div>
  );
};

export default Rope3D;
